import { useState } from "react";
import { api } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { compressImage } from "@/utils/imageCompression";
import {
  Camera, Loader2, Car, Users, ArrowRight, Plus, RefreshCw, FileCheck, ArrowLeftRight, Upload,
} from "lucide-react";

const SmartScanDialog = ({ open, onOpenChange, customers, onComplete }) => {
  const [scanning, setScanning] = useState(false);
  const [scanPreview, setScanPreview] = useState(null);
  const [scanResult, setScanResult] = useState(null);
  const [scanFile, setScanFile] = useState(null);
  const [selectedAction, setSelectedAction] = useState(null);
  const [transferCustomerId, setTransferCustomerId] = useState("");
  const [processing, setProcessing] = useState(false);

  const reset = () => {
    setScanning(false);
    setScanPreview(null);
    setScanResult(null);
    setScanFile(null);
    setSelectedAction(null);
    setTransferCustomerId("");
    setProcessing(false);
  };

  const handleClose = (o) => {
    if (!o) reset();
    onOpenChange(o);
  };

  const handleScan = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setScanning(true);
    setScanPreview(URL.createObjectURL(file));
    setScanResult(null);
    setSelectedAction(null);

    try {
      // Compress large smartphone photos before sending to LLM
      const compressed = await compressImage(file, { maxWidth: 1920, quality: 0.85 });
      setScanFile(compressed);

      const reader = new FileReader();
      const base64 = await new Promise((resolve) => {
        reader.onload = () => resolve(reader.result.split(",")[1]);
        reader.readAsDataURL(compressed);
      });

      const res = await api.post("/api/vehicles/smart-scan", {
        image_base64: base64,
        ocr_type: "registration",
      }, { timeout: 60000 });

      if (res.data.success) {
        setScanResult(res.data);
        toast.success("Fahrzeugschein erkannt!");
      } else {
        toast.error(res.data.error || "Scannen fehlgeschlagen");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Scannen");
      console.error(err);
    } finally {
      setScanning(false);
      e.target.value = "";
    }
  };

  const saveDocument = async (customerId) => {
    if (!scanFile || !customerId) return false;
    try {
      const compressed = await compressImage(scanFile, { maxWidth: 1920, quality: 0.85 });
      const form = new FormData();
      form.append("file", compressed);
      form.append("doc_type", "fahrzeugschein");
      await api.post(`/api/customers/${customerId}/documents`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      return true;
    } catch (err) {
      console.error("Document save failed:", err);
      toast.error(
        err.response?.data?.detail ||
          "Fahrzeugschein konnte nicht gespeichert werden. Bitte das Bild später erneut hochladen."
      );
      return false;
    }
  };

  const handleAction = async (action) => {
    if (!scanResult) return;
    setProcessing(true);

    const ocr = scanResult.ocr_data;
    const match = scanResult.vehicle_match;
    const customerMatch = scanResult.customer_matches?.[0];

    try {
      if (action === "save_doc") {
        // Just save document to existing customer
        const custId = match?.customer_id || customerMatch?.id;
        if (custId) {
          await saveDocument(custId);
          toast.success("Fahrzeugschein beim Kunden gespeichert");
        }
        handleClose(false);
        onComplete?.();

      } else if (action === "update") {
        // Update existing vehicle data
        const descParts = [];
        if (ocr.typ) descParts.push(ocr.typ);
        if (ocr.kraftstoff) descParts.push(ocr.kraftstoff);
        if (ocr.leistung_kw) descParts.push(`${ocr.leistung_kw} kW`);

        let regDate = null;
        if (ocr.erstzulassung) {
          const parts = ocr.erstzulassung.split(".");
          if (parts.length === 3) regDate = `${parts[2]}-${parts[1]}-${parts[0]}`;
        }

        const updateData = {};
        if (ocr.kennzeichen) updateData.license_plate = ocr.kennzeichen.toUpperCase();
        if (ocr.vin) updateData.vin = ocr.vin.toUpperCase().replace(/[^A-HJ-NPR-Z0-9]/g, "").slice(0, 17);
        if (ocr.marke) updateData.brand = ocr.marke;
        if (ocr.modell) updateData.model = ocr.modell;
        if (descParts.length) updateData.model_description = descParts.join(", ");
        if (regDate) updateData.first_registration = regDate;
        if (regDate) updateData.year = parseInt(regDate.split("-")[0]);

        await api.put(`/api/vehicles/${match.id}`, updateData);
        if (match.customer_id) await saveDocument(match.customer_id);
        toast.success("Fahrzeugdaten aktualisiert");
        handleClose(false);
        onComplete?.();

      } else if (action === "transfer") {
        if (!transferCustomerId) {
          toast.error("Bitte neuen Kunden auswählen");
          setProcessing(false);
          return;
        }
        await api.put(`/api/vehicles/${match.id}/transfer?new_customer_id=${transferCustomerId}`);
        await saveDocument(transferCustomerId);
        toast.success("Fahrzeug übertragen und Fahrzeugschein gespeichert");
        handleClose(false);
        onComplete?.();

      } else if (action === "create_new") {
        // Create new vehicle (go to vehicle form with pre-filled data)
        handleClose(false);
        onComplete?.("create", ocr, scanFile);

      } else if (action === "create_customer_and_vehicle") {
        // Create customer first, then vehicle
        const newCustomer = await api.post("/api/customers", {
          first_name: ocr.halter_vorname || "",
          last_name: ocr.halter_name || "",
          street: ocr.halter_strasse || "",
          postal_code: ocr.halter_plz || "",
          city: ocr.halter_ort || "",
          phone: "",
          email: "",
        });

        const custId = newCustomer.data.id;
        await saveDocument(custId);

        let regDate = null;
        let year = new Date().getFullYear();
        if (ocr.erstzulassung) {
          const parts = ocr.erstzulassung.split(".");
          if (parts.length === 3) {
            regDate = `${parts[2]}-${parts[1]}-${parts[0]}`;
            year = parseInt(parts[2]);
          }
        }

        const descParts = [];
        if (ocr.typ) descParts.push(ocr.typ);
        if (ocr.kraftstoff) descParts.push(ocr.kraftstoff);
        if (ocr.leistung_kw) descParts.push(`${ocr.leistung_kw} kW`);

        await api.post("/api/vehicles", {
          customer_id: custId,
          license_plate: (ocr.kennzeichen || "").toUpperCase(),
          brand: ocr.marke || "",
          model: ocr.modell || "",
          model_description: descParts.join(", "),
          vin: (ocr.vin || "").toUpperCase().replace(/[^A-HJ-NPR-Z0-9]/g, "").slice(0, 17),
          year: year,
          first_registration: regDate,
        });

        toast.success(`Kunde ${ocr.halter_vorname} ${ocr.halter_name} und Fahrzeug angelegt`);
        handleClose(false);
        onComplete?.();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler bei der Verarbeitung");
      console.error(err);
    } finally {
      setProcessing(false);
    }
  };

  const sortedCustomers = [...customers].sort((a, b) =>
    (`${a.last_name} ${a.first_name}`).localeCompare(`${b.last_name} ${b.first_name}`, "de")
  );

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto" data-testid="smart-scan-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Barlow Condensed" }} className="text-xl">
            Fahrzeugschein scannen
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {/* Upload Area */}
          {!scanResult && (
            <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-6 text-center">
              {scanning ? (
                <div className="flex flex-col items-center gap-3 py-4">
                  {scanPreview && <img src={scanPreview} alt="Scan" className="max-h-40 rounded object-contain" />}
                  <div className="flex items-center gap-2 text-primary">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span className="font-medium">Wird analysiert...</span>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-4 py-2">
                  <Camera className="w-10 h-10 text-muted-foreground" />
                  <p className="font-medium">Fahrzeugschein hinzufügen</p>
                  <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                    <label className="cursor-pointer flex-1 sm:flex-none">
                      <span className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 w-full" data-testid="smart-scan-camera-btn">
                        <Camera className="w-4 h-4" />
                        Foto aufnehmen
                      </span>
                      <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        className="hidden"
                        onChange={handleScan}
                        disabled={scanning}
                        data-testid="smart-scan-input"
                      />
                    </label>
                    <label className="cursor-pointer flex-1 sm:flex-none">
                      <span className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium border border-input bg-background hover:bg-accent w-full" data-testid="smart-scan-gallery-btn">
                        <Upload className="w-4 h-4" />
                        Aus Galerie
                      </span>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleScan}
                        disabled={scanning}
                      />
                    </label>
                  </div>
                  <p className="text-sm text-muted-foreground">Daten werden automatisch erkannt und abgeglichen</p>
                </div>
              )}
            </div>
          )}

          {/* Scan Results */}
          {scanResult && (
            <div className="space-y-4">
              {/* Preview */}
              {scanPreview && (
                <div className="flex justify-center">
                  <img src={scanPreview} alt="Fahrzeugschein" className="max-h-32 rounded shadow object-contain" />
                </div>
              )}

              {/* Erkannte Daten */}
              <Card data-testid="scan-results-card">
                <CardContent className="p-4 space-y-3">
                  <p className="font-semibold text-sm">Erkannte Daten:</p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    {scanResult.ocr_data.kennzeichen && (
                      <div><span className="text-muted-foreground">Kennzeichen:</span> <strong>{scanResult.ocr_data.kennzeichen}</strong></div>
                    )}
                    {scanResult.ocr_data.marke && (
                      <div><span className="text-muted-foreground">Marke:</span> <strong>{scanResult.ocr_data.marke}</strong></div>
                    )}
                    {scanResult.ocr_data.modell && (
                      <div><span className="text-muted-foreground">Modell:</span> <strong>{scanResult.ocr_data.modell}</strong></div>
                    )}
                    {scanResult.ocr_data.vin && (
                      <div className="col-span-2"><span className="text-muted-foreground">VIN:</span> <strong className="font-mono text-xs">{scanResult.ocr_data.vin}</strong></div>
                    )}
                    {scanResult.ocr_data.erstzulassung && (
                      <div><span className="text-muted-foreground">Erstzulassung:</span> <strong>{scanResult.ocr_data.erstzulassung}</strong></div>
                    )}
                    {scanResult.ocr_data.halter_name && (
                      <div><span className="text-muted-foreground">Halter:</span> <strong>{scanResult.ocr_data.halter_vorname} {scanResult.ocr_data.halter_name}</strong></div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Match Status */}
              {!scanResult.is_new_vehicle && scanResult.vehicle_match && (
                <Card className="border-blue-200 bg-blue-50/50" data-testid="vehicle-match-card">
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-center gap-2">
                      <Car className="w-5 h-5 text-blue-600" />
                      <p className="font-semibold text-sm">Fahrzeug bereits vorhanden</p>
                      <Badge variant="outline" className="ml-auto text-xs">
                        {scanResult.vehicle_match_type === "vin" ? "VIN" : "Kennzeichen"} erkannt
                      </Badge>
                    </div>
                    <p className="text-sm">
                      <strong>{scanResult.vehicle_match.brand} {scanResult.vehicle_match.model}</strong>
                      {" "}({scanResult.vehicle_match.license_plate})
                      {scanResult.vehicle_customer && (
                        <span className="text-muted-foreground">
                          {" "}– aktuell bei <strong>{scanResult.vehicle_customer.first_name} {scanResult.vehicle_customer.last_name}</strong>
                        </span>
                      )}
                    </p>

                    {/* Actions for existing vehicle */}
                    <div className="space-y-2 pt-2">
                      <Button
                        variant={selectedAction === "save_doc" ? "default" : "outline"}
                        className="w-full justify-start gap-2"
                        onClick={() => setSelectedAction("save_doc")}
                        data-testid="action-save-doc"
                      >
                        <FileCheck className="w-4 h-4" />
                        Nur Fahrzeugschein ablegen
                      </Button>
                      <Button
                        variant={selectedAction === "update" ? "default" : "outline"}
                        className="w-full justify-start gap-2"
                        onClick={() => setSelectedAction("update")}
                        data-testid="action-update"
                      >
                        <RefreshCw className="w-4 h-4" />
                        Fahrzeugdaten aktualisieren
                      </Button>
                      <Button
                        variant={selectedAction === "transfer" ? "default" : "outline"}
                        className="w-full justify-start gap-2"
                        onClick={() => setSelectedAction("transfer")}
                        data-testid="action-transfer"
                      >
                        <ArrowLeftRight className="w-4 h-4" />
                        Besitzerwechsel – an anderen Kunden übertragen
                      </Button>

                      {selectedAction === "transfer" && (
                        <div className="pl-6 pt-2 space-y-2">
                          <Label>Neuer Kunde:</Label>
                          <Select value={transferCustomerId} onValueChange={setTransferCustomerId}>
                            <SelectTrigger data-testid="transfer-customer-select">
                              <SelectValue placeholder="Kunden auswählen..." />
                            </SelectTrigger>
                            <SelectContent>
                              {sortedCustomers.map((c) => (
                                <SelectItem key={c.id} value={c.id}>
                                  {c.first_name} {c.last_name} {c.name ? `(${c.name})` : ""}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {scanResult.is_new_vehicle && (
                <Card className="border-green-200 bg-green-50/50" data-testid="new-vehicle-card">
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-center gap-2">
                      <Plus className="w-5 h-5 text-green-600" />
                      <p className="font-semibold text-sm">Neues Fahrzeug erkannt</p>
                    </div>

                    {scanResult.customer_matches?.length > 0 ? (
                      <>
                        <p className="text-sm">
                          Halter <strong>{scanResult.ocr_data.halter_vorname} {scanResult.ocr_data.halter_name}</strong> passt zu:
                          {" "}<strong>{scanResult.customer_matches[0].first_name} {scanResult.customer_matches[0].last_name}</strong>
                        </p>
                        <div className="space-y-2 pt-2">
                          <Button
                            variant={selectedAction === "create_new" ? "default" : "outline"}
                            className="w-full justify-start gap-2"
                            onClick={() => setSelectedAction("create_new")}
                            data-testid="action-create-existing-customer"
                          >
                            <Car className="w-4 h-4" />
                            Fahrzeug bei bestehendem Kunden anlegen
                          </Button>
                          <Button
                            variant={selectedAction === "create_customer_and_vehicle" ? "default" : "outline"}
                            className="w-full justify-start gap-2"
                            onClick={() => setSelectedAction("create_customer_and_vehicle")}
                            data-testid="action-create-new-customer"
                          >
                            <Users className="w-4 h-4" />
                            Neuen Kunden + Fahrzeug anlegen
                          </Button>
                        </div>
                      </>
                    ) : (
                      <div className="space-y-2 pt-2">
                        <p className="text-sm text-muted-foreground">
                          Kein passender Kunde gefunden.
                        </p>
                        <Button
                          variant={selectedAction === "create_customer_and_vehicle" ? "default" : "outline"}
                          className="w-full justify-start gap-2"
                          onClick={() => setSelectedAction("create_customer_and_vehicle")}
                          data-testid="action-create-all"
                        >
                          <Users className="w-4 h-4" />
                          Neuen Kunden + Fahrzeug aus Scan-Daten anlegen
                        </Button>
                        <Button
                          variant={selectedAction === "create_new" ? "default" : "outline"}
                          className="w-full justify-start gap-2"
                          onClick={() => setSelectedAction("create_new")}
                          data-testid="action-create-manual"
                        >
                          <Car className="w-4 h-4" />
                          Fahrzeug manuell einem Kunden zuweisen
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Confirm */}
              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => { reset(); }} className="flex-1" data-testid="scan-reset">
                  Erneut scannen
                </Button>
                <Button
                  onClick={() => handleAction(selectedAction)}
                  disabled={!selectedAction || processing}
                  className="flex-1 btn-primary gap-2"
                  data-testid="scan-confirm"
                >
                  {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                  Bestätigen
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SmartScanDialog;
